import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { appJotaiStore } from "../app-jotai";

import { cloudService } from "./cloudService";
import {
  currentCloudDocAtom,
  getCurrentCloudDoc,
  setCurrentCloudDoc,
} from "./currentDoc";
import { cloudSaveStatusAtom } from "./dialogState";

import type { ResolveShareResponse, VersionType } from "./types";

let apiRef: ExcalidrawImperativeAPI | null = null;

export const setExcalidrawAPIForCloud = (api: ExcalidrawImperativeAPI) => {
  apiRef = api;
};

const setStatus = (s: "idle" | "saving" | "saved" | "error") =>
  appJotaiStore.set(cloudSaveStatusAtom, s);

const sceneInputs = () => {
  if (!apiRef) {
    throw new Error("editor not ready");
  }
  return {
    elements: apiRef.getSceneElementsIncludingDeleted(),
    appState: apiRef.getAppState(),
    files: apiRef.getFiles(),
  };
};

/** Create a brand-new cloud doc (first save or Save as). */
export const saveAsNewCloudDoc = async (name: string, folderPath: string) => {
  setStatus("saving");
  try {
    const { elements, appState, files } = sceneInputs();
    await cloudService.createDoc(name, folderPath, elements, appState, files);
    // Auto-enable autosave once a drawing is saved to the cloud, so ongoing
    // edits persist without the user having to click save again. They can
    // still force a save via "Save version…".
    const current = getCurrentCloudDoc();
    if (current) {
      setCurrentCloudDoc({ ...current, autosave: true });
    }
    setStatus("saved");
  } catch (e) {
    setStatus("error");
    throw e;
  }
};

/** Update the current cloud doc (tagged save or autosave). */
export const saveCurrentCloudDoc = async (
  type: VersionType = "tagged",
  description: string | null = null,
) => {
  const current = getCurrentCloudDoc();
  if (!current) {
    throw new Error("no current cloud document");
  }
  setStatus("saving");
  try {
    const { elements, appState, files } = sceneInputs();
    const { versionId } = await cloudService.updateDoc(
      current.docId,
      type,
      description,
      elements,
      appState,
      files,
      current.autosave,
    );
    setCurrentCloudDoc({ ...current, currentVersionId: versionId });
    setStatus("saved");
  } catch (e) {
    setStatus("error");
    throw e;
  }
};

/** Whether the open drawing is backed by a cloud document. */
export const hasCurrentCloudDoc = (): boolean => getCurrentCloudDoc() != null;

/**
 * Start a brand-new drawing: reset the canvas and clear any cloud-document
 * association so the next "Save to cloud" prompts for a new name/location
 * instead of overwriting the previously open document.
 */
export const newDrawing = () => {
  if (apiRef) {
    apiRef.resetScene();
  }
  setCurrentCloudDoc(null);
};

/**
 * "Duplicate": keep the current canvas content exactly as-is, but clear the
 * cloud-document association so it behaves like a brand-new, unsaved drawing.
 * The next save (to disk or cloud) creates a new document rather than
 * overwriting the original. Autosave is turned off for the duplicate until it
 * is saved.
 */
export const duplicateDrawing = () => {
  setCurrentCloudDoc(null);
};

/** Toggle autosave on the current cloud doc. */
export const setAutosaveEnabled = async (enabled: boolean) => {
  const current = getCurrentCloudDoc();
  if (!current) {
    return;
  }
  setCurrentCloudDoc({ ...current, autosave: enabled });
  // Persist the flag with a lightweight tagged save so the server records it.
  if (enabled) {
    await saveCurrentCloudDoc("auto");
  }
};

// ---- autosave debounce ----------------------------------------------------

let autosaveTimer: number | null = null;
const AUTOSAVE_DEBOUNCE_MS = 8000;

/** Call on scene change; schedules a debounced autosave if enabled. */
export const scheduleAutosave = () => {
  const current = getCurrentCloudDoc();
  if (!current?.autosave) {
    return;
  }
  if (autosaveTimer) {
    window.clearTimeout(autosaveTimer);
  }
  autosaveTimer = window.setTimeout(() => {
    autosaveTimer = null;
    void saveCurrentCloudDoc("auto").catch(() => {
      /* status already set to error */
    });
  }, AUTOSAVE_DEBOUNCE_MS);
};

// ---- loading --------------------------------------------------------------

const loadSceneIntoCanvas = async (
  sceneUrl: string,
  fileUrls: Record<string, string>,
) => {
  if (!apiRef) {
    throw new Error("editor not ready");
  }
  const sceneJSON = await cloudService.fetchSceneJSON(sceneUrl);
  const data = JSON.parse(sceneJSON);

  apiRef.updateScene({
    elements: data.elements || [],
    appState: data.appState || {},
  });

  // Load referenced files (binary images) directly from their presigned URLs.
  const fileEntries = Object.entries(fileUrls);
  if (fileEntries.length) {
    const loaded = await Promise.all(
      fileEntries.map(async ([fileId, url]) => {
        const res = await fetch(url);
        const dataURL = await res.text();
        return {
          id: fileId,
          dataURL,
          mimeType: "image/png",
          created: Date.now(),
        };
      }),
    );
    // @ts-ignore - addFiles accepts BinaryFileData[]
    apiRef.addFiles(loaded);
  }
};

/** Open a cloud document into the canvas and set it as the current doc. */
export const openCloudDoc = async (docId: string) => {
  const doc = await cloudService.getDoc(docId);
  await loadSceneIntoCanvas(doc.downloads.scene, doc.downloads.files);
  setCurrentCloudDoc({
    docId: doc.docId,
    name: doc.name,
    folderPath: doc.folderPath,
    currentVersionId: doc.currentVersionId,
    autosave: doc.autosave,
  });
};

/** Open a shared snapshot as an editable LOCAL copy (no cloud doc association). */
export const openSharedSnapshot = async (share: ResolveShareResponse) => {
  await loadSceneIntoCanvas(share.downloads.scene, share.downloads.files);
  setCurrentCloudDoc(null); // editable local copy; not the viewer's doc
};

/**
 * Open a drawing from a local file (native file picker), mirroring the editor's
 * built-in "Open" behavior. Clears any cloud-doc association (it's a local file
 * now, not a cloud document).
 */
export const openFromFile = async () => {
  if (!apiRef) {
    throw new Error("editor not ready");
  }
  const { loadFromJSON } = await import("@excalidraw/excalidraw/data/json");
  const data = await loadFromJSON(
    apiRef.getAppState(),
    apiRef.getSceneElementsIncludingDeleted(),
  );
  apiRef.updateScene({
    elements: data.elements || [],
    appState: data.appState || {},
  });
  if (data.files) {
    // @ts-ignore - addFiles accepts BinaryFileData[]
    apiRef.addFiles(Object.values(data.files));
  }
  setCurrentCloudDoc(null);
};

export { currentCloudDocAtom };
